// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Turning a machine definition into a simulable world (M2-02).
//
// The loader is deliberately dull. Every interesting decision was made either in the format
// or in the simulation; this file's whole job is to be a faithful, order-preserving
// translation between them, and the one property it must never lose is **determinism of
// ordering**.
//
// That matters more than it looks. Collider index decides contact resolution order (PH-084),
// and device index decides the order device phases run in. Both are assigned here, from the
// order the arrays appear in the JSON document. So two clients loading the same bytes get
// the same indices, and a replay validated on the server resolves its contacts in the same
// sequence the player's browser did. Sort anything, or build from an object's key order, and
// scores stop being reproducible in a way no test of the physics would ever catch.
//
// Nothing here is on the hot path — it runs once, at load.

import { Flipper } from '../sim/Flipper'
import { World } from '../sim/World'
import { makeArc, makeCircle, makePolyline, makeSegment } from '../sim/colliders'
import type { Collider } from '../sim/colliders'
import {
  CaptiveBall,
  DropTarget,
  DropTargetBank,
  Gate,
  Kicker,
  Magnet,
  PopBumper,
  Ramp,
  Slingshot,
  Spinner,
  Trigger,
  WireGuide,
} from '../sim/devices'
import type { Device } from '../sim/devices'
import type { MaterialName, MaterialOverride } from '../sim/materials'
import { STEP_HZ } from '../sim/constants'
import { roundHalfAwayFromZero, toRadians } from '../math/deterministic'
import { assertValidMachine } from './validate'
import type {PlateProfile} from '../sim/Plate'
import {wireSupports} from './wireSupports'
import {slingCoverProfile} from './slingCovers'
import { resolveSettings, settingText } from './settings'
import type { ResolvedSettings } from './settings'
import type { BoundedPlane } from '../sim/World'
import type { TriggerRegion } from '../sim/devices'
import type {
  MachineDefinition,
  MachineDevice,
  MachineCollider,
  MachineLayer,
  Point2,
  Region,
} from './types'

/**
 * A format `Region` as the simulation wants one (04 §5a).
 *
 * One owner, because it has two callers now: a rollover's trigger volume and, since M9-45, a
 * bounded plane layer. The two shapes exist because the format is written for a person and the
 * hot path is written for arithmetic — `min`/`max` pairs against four scalars.
 */
function simRegion(region: Region): TriggerRegion {
  return region.shape === 'circle'
    ? { shape: 'circle', cx: region.centre[0], cy: region.centre[1], radius: region.radius }
    : {
        shape: 'rect',
        minX: region.min[0],
        minY: region.min[1],
        maxX: region.max[0],
        maxY: region.max[1],
      }
}

export interface LoadedMachine {
  readonly definition: MachineDefinition
  readonly world: World
  /** Flippers by definition id, for input binding. */
  readonly flippers: ReadonlyMap<string, Flipper>
  /** Simulation devices by definition id, for the rule engine's coil actions. */
  readonly devices: ReadonlyMap<string, Device>
  /** Layer id → layer index, as the simulation numbers them. */
  readonly layerIndex: ReadonlyMap<string, number>
  /**
   * Device kinds present in the definition that this build has no simulation for yet.
   *
   * Reported rather than skipped in silence. A machine that quietly loses its captive ball
   * still plays — it just plays a different machine than the author built, and the author
   * has no way to find out.
   */
  readonly unsupported: readonly { readonly id: string; readonly kind: string }[]
}

/** Milliseconds to simulation steps. Negative values pass through as sentinels. */
function msToSteps(ms: number): number {
  if (ms < 0) return -1
  return roundHalfAwayFromZero(ms * (STEP_HZ / 1000))
}

function pt(p: Point2): readonly [number, number] {
  return [p[0], p[1]]
}

/**
 * Build a world from a definition.
 *
 * Validates first and throws on failure: a partially-loaded machine is worse than none,
 * because it looks like it worked.
 */
export function loadMachine(doc: unknown): LoadedMachine {
  const definition = assertValidMachine(doc)

  // --- layers ----------------------------------------------------------------
  // Index 0 is the playfield by convention, matching Ball.layer's default.
  const layerIndex = new Map<string, number>()
  const orderedLayers: MachineLayer[] = []
  for (const layer of definition.layers) {
    layerIndex.set(layer.id, orderedLayers.length)
    orderedLayers.push(layer)
  }

  const colliders: Collider[] = []
  const devices: Device[] = []
  const flippers: Flipper[] = []
  const byId = new Map<string, Device>()
  const flipperById = new Map<string, Flipper>()
  const unsupported: { id: string; kind: string }[] = []

  // --- devices, before colliders ---------------------------------------------
  // Device index has to exist before a collider can name a device, and `World` asks each
  // device for its own colliders, so devices are built first and static geometry second.
  const dropTargets = new Map<string, DropTarget>()

  for (const device of definition.devices) {
    const built = buildDevice(device, dropTargets, unsupported)
    if (built === undefined) continue
    if (built instanceof Kicker && (device.kind === 'kicker' || device.kind === 'plunger' || device.kind === 'autoPlunger')) {
      built.exitLayer = device.exit.to === undefined ? -1 : layerIndex.get(device.exit.to) ?? -1
      built.lockable = device.lockable === true
    }
    if (built instanceof Flipper) {
      if (device.layer !== undefined) built.contactLayer = layerIndex.get(device.layer) ?? 0
      flippers.push(built)
      flipperById.set(device.id, built)
      continue
    }
    if (device.layer !== undefined) built.contactLayer = layerIndex.get(device.layer) ?? 0
    built.index = devices.length
    devices.push(built)
    byId.set(device.id, built)
  }

  // Banks reference targets by id, so they are built in a second pass once every target
  // exists — otherwise a bank declared above its targets would silently find none.
  for (const device of definition.devices) {
    if (device.kind !== 'dropTargetBank') continue
    const targets: DropTarget[] = []
    for (const id of device.targets) {
      const t = dropTargets.get(id)
      if (t !== undefined) targets.push(t)
    }
    if (targets.length === 0) continue
    const config: { id: string; targets: DropTarget[]; autoResetSteps?: number } = {
      id: device.id,
      targets,
    }
    if (device.autoResetMs !== undefined) config.autoResetSteps = msToSteps(device.autoResetMs)
    const bank = new DropTargetBank(config)
    bank.index = devices.length
    devices.push(bank)
    byId.set(device.id, bank)
  }

  // --- ramps and habitrails, which are layers rather than devices -------------
  for (const layer of orderedLayers) {
    if (layer.kind === 'ramp') {
      const own = layerIndex.get(layer.id) ?? 0
      const ramp = new Ramp({
        id: layer.id,
        spline: layer.spline.map((p) => [p[0], p[1], p[2]] as const),
        layer: own,
        fromLayer: layerIndex.get(layer.entry.from) ?? 0,
        toLayer: layerIndex.get(layer.exit.to) ?? 0,
        width: layer.width,
      })
      ramp.index = devices.length
      devices.push(ramp)
      byId.set(layer.id, ramp)
    } else if (layer.kind === 'wire') {
      const wire = new WireGuide({
        id: layer.id,
        supports:layer.support===undefined?[]:wireSupports(layer.support,layer.spline),
        spline: layer.spline.map((p) => [p[0], p[1], p[2]] as const),
        captureRadius: layer.captureRadius,
        fromLayer: layerIndex.get(layer.id) ?? 0,
        toLayer: layerIndex.get(layer.exit.to) ?? 0,
      })
      wire.index = devices.length
      devices.push(wire)
      byId.set(layer.id, wire)
    }
  }

  // --- static geometry ---------------------------------------------------------
  //
  // Resolved once, here, rather than per collider: the angle is the machine's, and converting
  // degrees to radians inside the loop would be the same division done thirty-odd times with
  // thirty-odd chances to round differently on a different runtime.
  const outlaneScatter =
    definition.physics?.outlaneScatterDegrees !== undefined
      ? toRadians(definition.physics.outlaneScatterDegrees)
      : -1
  // The operator menu, resolved once. Read here for exactly one thing — a collider that declares
  // `adjustedBy` (04 §6, M9-36) — and passed down rather than re-read per collider, for the same
  // reason as the scatter angle above.
  const settings = resolveSettings(definition)
  for (const c of definition.colliders) {
    colliders.push(...buildCollider(c, layerIndex, byId, outlaneScatter, settings))
  }

  // --- physics overrides ---------------------------------------------------------
  const options: {
    colliders: Collider[]
    devices: Device[]
    flippers: Flipper[]
    ballCount: number
    glassHeight: number
    seed?: number
    materialOverrides?: Partial<Record<MaterialName, MaterialOverride>>
    speedClamp?: number
    boundedPlanes?: BoundedPlane[]
    homePlane?: number
  } = {
    colliders,
    devices,
    flippers,
    ballCount: definition.physics?.ballCount ?? 1,
    glassHeight: definition.dimensions.glassHeight,
  }
  // Declared in the format and validated since M2, and until now it reached nothing: the world
  // clamped at the core's own `SPEED_CLAMP` whatever the machine asked for. NEON NOVA happens
  // to ask for 8.0, which is the default, so the omission was invisible on the only machine
  // that exercised it — and would have surfaced as a Builder machine whose cannon shot balls
  // through walls.
  if (definition.physics?.ballSpeedClamp !== undefined) {
    options.speedClamp = definition.physics.ballSpeedClamp
  }
  const materials = definition.physics?.materials
  if (materials !== undefined) {
    const overrides: Partial<Record<MaterialName, MaterialOverride>> = {}
    // Sorted, so the override map is built in the same order on every runtime. The values
    // are independent, but a reproducible build order costs nothing and removes a question.
    for (const name of Object.keys(materials).sort()) {
      const value = materials[name as MaterialName]
      if (value !== undefined) overrides[name as MaterialName] = value
    }
    options.materialOverrides = overrides
  }

  // 04 §5's `layers[].bounds`, which was declared, validated as an optional field and read by
  // nothing until M9-45 — so §5's own `shooterLane` example was inert and a bounded plane was an
  // unbounded one. The first plane with no bounds is home; every bounded plane is offered a ball
  // in declaration order.
  //
  // **Both shipped machines declare exactly one plane and no bounds**, so both lists below come
  // out empty and `World.step` skips the whole feature — which is why this moved no fixture.
  const boundedPlanes: BoundedPlane[] = []
  let homePlane: number | undefined
  for (const layer of orderedLayers) {
    if (layer.kind !== 'plane') continue
    const index = layerIndex.get(layer.id) ?? 0
    if (layer.bounds === undefined) {
      if (homePlane === undefined) homePlane = index
      continue
    }
    boundedPlanes.push({ layer: index, region: simRegion(layer.bounds), ...(layer.minEntryZ === undefined ? {} : { minEntryZ: layer.minEntryZ, floorZ: layer.z }) })
  }
  if (boundedPlanes.length > 0) {
    options.boundedPlanes = boundedPlanes
    // `undefined` cannot happen on a valid document — `no-playfield` refuses a machine with no
    // plane, and a machine whose *only* planes are bounded has nowhere for a ball outside them to
    // be. Defaulted rather than thrown because `loadMachine` runs on documents the Builder has not
    // finished, and 0 is the convention every other layer index falls back to.
    options.homePlane = homePlane ?? 0
  }

  const world = new World(options)

  return {
    definition,
    world,
    flippers: flipperById,
    devices: byId,
    layerIndex,
    unsupported,
  }
}

/**
 * How far an operator setting moves this collider, in metres, on each axis (M9-36).
 *
 * `[0, 0]` for every collider that declares no adjustment and for every option the machine's
 * settings block does not resolve to — so a definition written before this existed builds the
 * geometry it always built, which is what let this land without moving a fixture.
 *
 * **The offset is looked up by the setting's *resolved value*.** A validator refuses a machine
 * whose adjustment does not offer every declared option, so a missing entry here means the
 * document reached the loader without passing validation — the Builder calls `loadMachine` on
 * every keystroke — and the honest answer to that is "do not move", not "throw".
 */
function adjustmentOf(c: MachineCollider, settings: ResolvedSettings): readonly [number, number] {
  const adjust = c.adjustedBy
  if (adjust === undefined) return [0, 0]
  const chosen = settingText(settings, adjust.setting, '')
  const by = adjust.offsets[chosen]
  if (by === undefined || !Number.isFinite(by)) return [0, 0]
  return adjust.axis === 'x' ? [by, 0] : [0, by]
}

function buildCollider(
  c: MachineCollider,
  layerIndex: ReadonlyMap<string, number>,
  byId: ReadonlyMap<string, Device>,
  outlaneScatter: number,
  settings: ResolvedSettings,
): Collider[] {
  const layer = layerIndex.get(c.layer) ?? 0
  const [dx, dy] = adjustmentOf(c, settings)
  const device = c.device !== undefined ? byId.get(c.device)?.index ?? -1 : -1
  // −1 means "use the material's own angle", which is also what an unmarked surface gets and
  // what a marked one gets on a machine that never set the number.
  const scatter = c.outlane === true ? outlaneScatter : -1

  switch (c.kind) {
    case 'segment': {
      const options: {
        layer: number
        device?: number
        oneSided?: boolean
        normal?: readonly [number, number]
        scatter?: number
        padding?:number
      } = { layer, scatter,padding:(c.thickness??0)/2 }
      if (device >= 0) options.device = device
      if (c.oneSided === true) options.oneSided = true
      if (c.normal !== undefined) options.normal = pt(c.normal)
      return [makeSegment(c.a[0] + dx, c.a[1] + dy, c.b[0] + dx, c.b[1] + dy, c.material, options)]
    }
    case 'arc': {
      const options: { layer: number; device?: number; insideOut: boolean; scatter: number;padding?:number } = {
        layer,
        insideOut: c.insideOut,
        padding:(c.thickness??0)/2,
        scatter,
      }
      if (device >= 0) options.device = device
      return [
        makeArc(c.centre[0] + dx, c.centre[1] + dy, c.radius, c.angleFrom, c.angleTo, c.material, options),
      ]
    }
    case 'circle': {
      const options: { layer: number; device?: number; scatter: number } = { layer, scatter }
      if (device >= 0) options.device = device
      return [makeCircle(c.centre[0] + dx, c.centre[1] + dy, c.radius, c.material, options)]
    }
    case 'polyline': {
      const options: { layer: number; device?: number; scatter: number;padding?:number } = { layer, scatter,padding:(c.thickness??0)/2 }
      if (device >= 0) options.device = device
      return [...makePolyline(c.points.map((p) => [p[0] + dx, p[1] + dy] as const), c.material, options)]
    }
  }
}

/**
 * Build the simulation device for one definition device.
 *
 * Returns a `Flipper` (which is not a `Device` — it has its own phase in the world loop),
 * a `Device`, or `undefined` for a kind this build cannot simulate yet.
 */
function buildDevice(
  d: MachineDevice,
  dropTargets: Map<string, DropTarget>,
  unsupported: { id: string; kind: string }[],
): Device | Flipper | undefined {
  switch (d.kind) {
    case 'flipper': {
      const config: {
        pivotX: number
        pivotY: number
        restAngle: number
        direction: 1 | -1
        sweep: number
        length: number
        radiusPivot: number
        radiusTip: number
        strength?: number
        returnTorque?: number
        coilRampSeconds?: number
        eosAngle?: number
        eosTorqueRatio?: number
      } = {
        pivotX: d.pivot[0],
        pivotY: d.pivot[1],
        restAngle: d.restAngle,
        // A left flipper rotates counter-clockwise to fire. `handedness` says which bat this
        // is; the sign is the simulation's way of saying the same thing.
        direction: d.handedness === 'left' ? 1 : -1,
        sweep: d.sweep,
        length: d.length,
        radiusPivot: d.radiusPivot,
        radiusTip: d.radiusTip,
      }
      if (d.strength !== undefined) config.strength = d.strength
      if (d.returnTorque !== undefined) config.returnTorque = d.returnTorque
      // The three feel parameters. Declared in the format and documented in 04 §7 since the
      // format existed, and passed to nothing until now — a machine author could set any of
      // them and the bat would behave identically, which is the worst kind of setting to
      // offer: one that looks like it works.
      //
      // `coilRampUp` is in VPX's units of roughly ten milliseconds (02 §6.1), so 2.5 is the
      // 25 ms a real solenoid takes to build its field, and that ramp is exactly what makes a
      // tap pass possible.
      if (d.coilRampUp !== undefined) config.coilRampSeconds = d.coilRampUp / 100
      if (d.eosTorque !== undefined) config.eosTorqueRatio = d.eosTorque
      if (d.eosAngleDegrees !== undefined) config.eosAngle = toRadians(d.eosAngleDegrees)
      return new Flipper(config)
    }

    case 'popBumper': {
      const config: {
        id: string
        centre: readonly [number, number]
        skirtRadius: number
        kickSpeed: number
        cooldownSteps?: number
      } = { id: d.id, centre: pt(d.centre), skirtRadius: d.skirtRadius, kickSpeed: d.kickSpeed }
      if (d.cooldownMs !== undefined) config.cooldownSteps = msToSteps(d.cooldownMs)
      return new PopBumper(config)
    }

    case 'slingshot': {
      const config: {
        id: string
        a: readonly [number, number]
        b: readonly [number, number]
        kickSpeed: number
        triggerSpeed?: number
        cover?:readonly PlateProfile[]
      } = { id: d.id, a: pt(d.a), b: pt(d.b), kickSpeed: d.kickSpeed }
      if(d.cover!==undefined)config.cover=slingCoverProfile(d.cover)
      if (d.triggerSpeed !== undefined) config.triggerSpeed = d.triggerSpeed
      return new Slingshot(config)
    }

    case 'dropTarget': {
      const config: {
        id: string
        a: readonly [number, number]
        b: readonly [number, number]
        dropSpeed?: number
        solidBack?: boolean
      } = { id: d.id, a: pt(d.a), b: pt(d.b) }
      if (d.solidBack !== undefined) config.solidBack = d.solidBack
      if (d.dropSpeed !== undefined) config.dropSpeed = d.dropSpeed
      const target = new DropTarget(config)
      dropTargets.set(d.id, target)
      return target
    }

    case 'standupTarget': {
      // A standup does not drop. Modelled as a target that no shot can knock over, which is
      // exactly what it is: a one-sided face wired to a switch.
      return new DropTarget({ id: d.id, a: pt(d.a), b: pt(d.b), solidBack: d.solidBack === true, dropSpeed: Number.POSITIVE_INFINITY })
    }

    // Built in a second pass, once every target it names exists.
    case 'dropTargetBank':
      // Every other kind in `DEVICE_KINDS` now has a simulation, so `unsupported` comes back
      // empty for any valid definition. The channel stays: the next kind added to the format
      // arrives before its mechanism does, and a machine that quietly loses a device still
      // plays — it just plays a different machine than its author built.
      void unsupported
      return undefined

    case 'spinner':
      return new Spinner({
        id: d.id,
        a: pt(d.a),
        b: pt(d.b),
        vaneMass: d.vaneMass,
        shaftHeight: d.shaftHeight,
        vaneDrop: d.vaneDrop,
        damping: d.damping,
      })

    case 'kicker':
    case 'plunger':
    case 'autoPlunger': {
      const config: {
        id: string
        centre: readonly [number, number]
        captureRadius: number
        holdSteps: number
        exitDirection: readonly [number, number]
        exitSpeed: number
        captureSpeed?: number
        rearmSteps?: number
      } = {
        id: d.id,
        centre: pt(d.centre),
        captureRadius: d.captureRadius,
        holdSteps: msToSteps(d.holdMs),
        exitDirection: pt(d.exit.direction),
        exitSpeed: d.exit.speed,
      }
      if (d.captureSpeed !== undefined) config.captureSpeed = d.captureSpeed
      if (d.rearmMs !== undefined) config.rearmSteps = msToSteps(d.rearmMs)
      return new Kicker(config)
    }

    case 'magnet':
      return new Magnet({
        id: d.id,
        centre: pt(d.centre),
        radius: d.radius,
        peakForce: d.peakForce,
        releaseAfterSteps: d.releaseAfterMs===undefined ? 0 : msToSteps(d.releaseAfterMs),
      })

    case 'gate':
    case 'ballSaveGate':
    case 'diverter': {
      const config: {
        id: string
        a: readonly [number, number]
        b: readonly [number, number]
        passDirection: readonly [number, number]
        mountContacts?:boolean
        armMass?: number
      } = { id: d.id, a: pt(d.a), b: pt(d.b), passDirection: pt(d.passDirection),mountContacts:d.mountContacts??false }
      if (d.armMass !== undefined) config.armMass = d.armMass
      return new Gate(config)
    }

    case 'rollover': {
      const region = simRegion(d.region)
      const config: { id: string; region: TriggerRegion; debounceSteps?: number; zRange?:readonly [number,number] } = {
        id: d.id,
        region,
      }
      if (d.debounceMs !== undefined) config.debounceSteps = msToSteps(d.debounceMs)
      if (d.zRange !== undefined) config.zRange = d.zRange
      return new Trigger(config)
    }

    case 'captiveBall':
      return new CaptiveBall({
        cageContacts: d.cageContacts??false,
        id: d.id,
        centre: pt(d.centre),
        travel: d.travel,
        direction: pt(d.direction),
      })
  }
}
